

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

import { API_BASE } from "./utils";

export const FIELD_TYPES = [
  "text",
  "paragraph",
  "textarea",
  "email",
  "number",
  "url",
  "dropdown",
  "select",
  "multiselect",
  "checkbox",
  "checkbox_group",
  "yes_no",
  "radio",
  "date",
  "datetime",
  "time",
  "phone",
  "address",
  "country",
  "rating",
  "linear_scale",
  "nps",
  "short_answer",
  "long_answer",
  "consent",
  "hidden",
  "section",
  "file",
];

export function createDefaultField(type = "text", index = 0) {
  const id = `f_${Date.now()}_${index}`;
  const optionTypes = new Set(["select", "dropdown", "multiselect", "checkbox_group", "radio", "yes_no", "linear_scale", "rating", "nps", "country"]);
  const optionDefaults = {
    yes_no: ["Yes", "No"],
    linear_scale: ["1", "2", "3", "4", "5"],
    rating: ["1", "2", "3", "4", "5"],
    nps: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
    country: ["Singapore", "United States", "United Kingdom", "Australia", "Japan"],
  };
  const labelMap = {
    paragraph: "Paragraph text",
    dropdown: "Dropdown",
    checkbox_group: "Checkbox group",
    datetime: "Date & time",
    short_answer: "Short answer",
    long_answer: "Long answer",
    consent: "Consent",
    linear_scale: "Linear scale",
    nps: "NPS score",
  };
  return {
    id,
    key: id,
    type,
    label: `New ${labelMap[type] || type} field`,
    required: false,
    options: optionTypes.has(type) ? optionDefaults[type] || ["Option 1", "Option 2"] : [],
    helpText: "",
    placeholder: "",
    ticketScope: [],
    visibilityRule: { logic: "AND", conditions: [] },
    requiredRule: { logic: "AND", conditions: [] },
    sortOrder: index,
  };
}

export function normalizeFormState(raw) {
  const fields = Array.isArray(raw?.fields) ? raw.fields : [];
  const out = fields.map((f, idx) => ({
    ...createDefaultField(f?.type || "text", idx),
    ...f,
    id: String(f?.id || `f_${idx}`),
    key: String(f?.key || f?.id || `f_${idx}`),
    label: String(f?.label || "Untitled field"),
    required: Boolean(f?.required),
    options: Array.isArray(f?.options) ? f.options.map((x) => String(x).trim()).filter(Boolean) : [],
    ticketScope: Array.isArray(f?.ticketScope) ? f.ticketScope : [],
    visibilityRule:
      f?.visibilityRule && typeof f.visibilityRule === "object" ? f.visibilityRule : { logic: "AND", conditions: [] },
    requiredRule:
      f?.requiredRule && typeof f.requiredRule === "object" ? f.requiredRule : { logic: "AND", conditions: [] },
    sortOrder: Number(f?.sortOrder ?? idx),
  }));
  return {
    fields: out.sort((a, b) => a.sortOrder - b.sortOrder),
    active: raw?.active || out[0]?.id || null,
    version: Number(raw?.version || 1),
    publishedAt: raw?.publishedAt || null,
  };
}

export async function fetchFormDraft(eventId) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/form-builder/draft`);
  if (!res.ok) throw new Error("Could not load form draft");
  const payload = await res.json();
  return normalizeFormState(payload?.value || {});
}

export async function saveFormDraft(eventId, state) {
  const payload = normalizeFormState(state);
  const res = await fetch(`${API_BASE}/api/events/${eventId}/form-builder/draft`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Could not save form draft");
  const body = await res.json();
  return normalizeFormState(body?.value || payload);
}

export async function publishForm(eventId) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/form-builder/publish`, { method: "POST" });
  if (!res.ok) throw new Error("Could not publish form");
  const payload = await res.json();
  return normalizeFormState(payload?.value || {});
}

export async function fetchPublishedForm(eventId) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/form-builder/published`);
  if (!res.ok) throw new Error("Could not load published form");
  const payload = await res.json();
  if (!payload?.value) return null;
  return normalizeFormState(payload.value);
}

export function evaluateRule(rule, ticketId, ticketName, answers) {
  if (!rule || !Array.isArray(rule.conditions) || !rule.conditions.length) return true;
  const logic = String(rule.logic || "AND").toUpperCase();
  const evalCondition = (condition) => {
    const source = String(condition?.source || "answer");
    const op = String(condition?.op || "equals");
    const target = condition?.value;
    const current = source === "ticket" ? ticketId || ticketName : answers?.[condition?.fieldId];
    if (op === "equals") return current === target;
    if (op === "not_equals") return current !== target;
    if (op === "contains") return Array.isArray(current) ? current.includes(target) : String(current || "").includes(String(target || ""));
    if (op === "not_empty") return Array.isArray(current) ? current.length > 0 : Boolean(String(current || "").trim());
    if (op === "empty") return Array.isArray(current) ? current.length === 0 : !Boolean(String(current || "").trim());
    return false;
  };
  const values = rule.conditions.map(evalCondition);
  return logic === "OR" ? values.some(Boolean) : values.every(Boolean);
}

export function resolveVisibleFields(fields, ticketId, ticketName, answers) {
  return fields.filter((field) => {
    if (Array.isArray(field.ticketScope) && field.ticketScope.length) {
      if (!field.ticketScope.includes(ticketId) && !field.ticketScope.includes(ticketName)) return false;
    }
    return evaluateRule(field.visibilityRule, ticketId, ticketName, answers);
  });
}

